import * as Sentry from '@sentry/nextjs';

/**
 * Markdown 相关错误的类型
 */
export enum MarkdownErrorType {
  FETCH_ERROR = 'FETCH_ERROR',
  HTTP_CLIENT_ERROR = 'HTTP_CLIENT_ERROR',
  HTTP_SERVER_ERROR = 'HTTP_SERVER_ERROR',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  INVALID_INPUT = 'INVALID_INPUT', // 用于无效的输入数据，如不正确的URL格式
}

/**
 * Markdown 错误类
 * 可以包含原始错误信息，错误类型，以及其他自定义字段
 */
export class MarkdownError extends Error {
  public readonly originalError?: Error;
  public readonly errorType: MarkdownErrorType;
  public readonly url?: string;
  public readonly statusCode?: number;

  constructor(
    message: string,
    errorType: MarkdownErrorType,
    options?: {
      originalError?: Error;
      url?: string;
      statusCode?: number;
    }
  ) {
    super(message);
    this.name = 'MarkdownError';
    this.errorType = errorType;
    this.originalError = options?.originalError;
    this.url = options?.url;
    this.statusCode = options?.statusCode;
    this.message = this.getFullMessage(message);

    // 确保原型链正确
    Object.setPrototypeOf(this, MarkdownError.prototype);

    // 捕获堆栈跟踪（仅在 V8 引擎中有效）
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, MarkdownError);
    }
  }

  /**
   * 获取完整的错误信息，包括错误类型、URL、状态码和原始错误（如果存在）
   */
  public getFullMessage(message: string): string {
    let fullMessage = `${this.errorType}: ${message}`;
    if (this.url) {
      fullMessage += `\nURL：${this.url}`;
    }
    if (this.statusCode) {
      fullMessage += `\n状态码：${this.statusCode}`;
    }
    if (this.originalError) {
      fullMessage += `\n原始错误：${this.originalError.message}`;
    }
    return fullMessage;
  }

  /**
   * 将错误转换为适合 Sentry 的格式
   */
  public toSentry(): Sentry.Event {
    return {
      message: this.message,
      level: 'error' as Sentry.SeverityLevel,
      extra: {
        errorType: this.errorType,
        url: this.url,
        statusCode: this.statusCode,
        originalError: this.originalError?.message,
      },
    };
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      errorType: this.errorType,
      url: this.url,
      statusCode: this.statusCode,
      originalError: this.originalError?.message,
    };
  }
}
